import { useIntersectionObserver } from '@vueuse/core';
import { computed, ref, type Ref } from 'vue';

/**
 * Баннер экскурсий на планшете и мобильном: сначала стоит в потоке на своём месте, как только целиком
 * показался у нижнего края экрана, подменяется прилипающим, который остаётся внизу до конца контента,
 * а доехав до своего места в конце, снова становится обычным блоком.
 *
 * Из потока баннер при подмене убирается, обёртка схлопывается в ноль, но остаётся на месте:
 * по ней же ловим возврат вверх.
 */
export const usePinnedBanner = (isDesktopSmall: Ref<boolean>) => {
  const inlineBannerWrap = ref<HTMLElement | null>(null);
  const isInlineBannerReached = ref(false);

  useIntersectionObserver(
    inlineBannerWrap,
    (entries) => {
      const entry = entries[entries.length - 1];

      if (!entry) {
        return;
      }

      // нижний край обёртки не ниже нижнего края экрана: баннер виден целиком или уже выше
      isInlineBannerReached.value = entry.boundingClientRect.bottom <= (entry.rootBounds?.bottom ?? window.innerHeight);
    },
    { threshold: 1 },
  );

  // маркер нулевой высоты сразу за прилипающим баннером: когда он попал в экран, баннер доехал
  // до своего места в конце контента и перестаёт быть «прилипшим»
  const pinnedBannerEnd = ref<HTMLElement | null>(null);
  const isPinnedBannerAtEnd = ref(false);

  useIntersectionObserver(pinnedBannerEnd, (entries) => {
    const entry = entries[entries.length - 1];

    if (!entry) {
      return;
    }

    isPinnedBannerAtEnd.value = entry.boundingClientRect.top <= (entry.rootBounds?.bottom ?? window.innerHeight);
  });

  const showPinnedBanner = computed(() => !isDesktopSmall.value && isInlineBannerReached.value);

  return { inlineBannerWrap, pinnedBannerEnd, showPinnedBanner, isPinnedBannerAtEnd };
};
